/**
 * Events Hook
 * Reusable hooks for events data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api/api.client';
import { API_ENDPOINTS, buildUrl } from '@/api/endpoints';
import type {
    Event,
    EventType,
    ApiResponse,
    CreateEventRequest,
    UpdateEventRequest,
} from '@/api/types';

// Query keys
export const EVENTS_KEY = ['events'];
export const EVENT_KEY = (id: string) => ['events', id];
export const EVENT_SLUG_KEY = (slug: string) => ['events', 'slug', slug];

// Public stats key - must invalidate when events change (affects totalImpact)
const PUBLIC_STATS_KEY = ['public-stats'];

// Types for query params
export interface EventsQueryParams {
    page?: number;
    limit?: number;
    published?: boolean;
    type?: EventType;
    upcoming?: boolean;
    search?: string;
}

// Backend events response structure
interface EventsApiResponse {
    success: boolean;
    data: Event[];
    meta: {
        timestamp: string;
        path: string;
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    };
}

// Return type for events query
interface EventsPaginatedResult {
    data: Event[];
    meta: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

/**
 * Fetch all events with optional filtering
 */
export function useEvents(params?: EventsQueryParams) {
    return useQuery<EventsApiResponse, Error, EventsPaginatedResult>({
        queryKey: [...EVENTS_KEY, params],
        queryFn: async () => {
            const queryParams: Record<string, unknown> = {
                page: params?.page || 1,
                limit: params?.limit || 10,
            };

            if (params?.published !== undefined) {
                queryParams.published = params.published;
            }

            if (params?.type) {
                queryParams.type = params.type;
            }

            if (params?.upcoming !== undefined) {
                queryParams.upcoming = params.upcoming;
            }

            if (params?.search) {
                queryParams.search = params.search;
            }

            const url = buildUrl(API_ENDPOINTS.EVENTS.BASE, queryParams as Record<string, string | number | boolean | undefined>);
            const response = await apiClient.get<EventsApiResponse>(url);
            return response.data;
        },
        select: (apiResponse) => ({
            data: apiResponse.data,
            meta: apiResponse.meta.pagination,
        }),
        staleTime: 30000,
        refetchOnMount: 'always',
    });
}

/**
 * Fetch upcoming published events for public display
 */
export function usePublicEvents(params?: { limit?: number }) {
    return useEvents({
        published: true,
        upcoming: true,
        limit: params?.limit || 10,
    });
}

/**
 * Count of upcoming published events (for nav badge)
 */
export function useUpcomingEventsCount() {
    return useEvents({
        published: true,
        upcoming: true,
        limit: 1,
        page: 1,
    });
}

/**
 * Fetch a single event by slug (for public)
 */
export function useEventBySlug(slug: string) {
    return useQuery<ApiResponse<Event>, Error, Event>({
        queryKey: EVENT_SLUG_KEY(slug),
        queryFn: async () => {
            const response = await apiClient.get<ApiResponse<Event>>(
                API_ENDPOINTS.EVENTS.BY_SLUG(slug)
            );
            return response.data;
        },
        select: (data) => data.data,
        enabled: !!slug,
        refetchOnMount: 'always',
    });
}

/**
 * Fetch a single event by ID (for admin editing)
 */
export function useEvent(id: string) {
    return useQuery<ApiResponse<Event>, Error, Event>({
        queryKey: EVENT_KEY(id),
        queryFn: async () => {
            const response = await apiClient.get<ApiResponse<Event>>(
                API_ENDPOINTS.EVENTS.BY_ID(id)
            );
            return response.data;
        },
        select: (data) => data.data,
        enabled: !!id,
    });
}

/**
 * Create a new event
 */
export function useCreateEvent() {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<Event>, Error, CreateEventRequest>({
        mutationFn: async (data: CreateEventRequest) => {
            const response = await apiClient.post<ApiResponse<Event>>(
                API_ENDPOINTS.EVENTS.BASE,
                data
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
            // Invalidate public stats (totalImpact includes event attendees)
            queryClient.invalidateQueries({ queryKey: PUBLIC_STATS_KEY });
        },
    });
}

/**
 * Update an event
 */
export function useUpdateEvent() {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<Event>, Error, { id: string; data: UpdateEventRequest }>({
        mutationFn: async ({ id, data }) => {
            const response = await apiClient.patch<ApiResponse<Event>>(
                API_ENDPOINTS.EVENTS.BY_ID(id),
                data
            );
            return response.data;
        },
        onSuccess: (response, { id }) => {
            queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
            queryClient.invalidateQueries({ queryKey: EVENT_KEY(id) });
            // Also invalidate slug-based query if available
            if (response.data?.slug) {
                queryClient.invalidateQueries({ queryKey: EVENT_SLUG_KEY(response.data.slug) });
            }
            // Invalidate bookings since event data changed
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            // Invalidate public stats (totalImpact includes event attendees)
            queryClient.invalidateQueries({ queryKey: PUBLIC_STATS_KEY });
        },
    });
}

/**
 * Delete an event
 */
export function useDeleteEvent() {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: async (id: string) => {
            await apiClient.delete(API_ENDPOINTS.EVENTS.BY_ID(id));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            queryClient.invalidateQueries({ queryKey: PUBLIC_STATS_KEY });
            queryClient.invalidateQueries({ queryKey: ['trash'] });
        },
    });
}

/**
 * Toggle event published status
 */
export function useToggleEventPublished() {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<Event>, Error, string>({
        mutationFn: async (id: string) => {
            const response = await apiClient.patch<ApiResponse<Event>>(
                API_ENDPOINTS.EVENTS.PUBLISH(id)
            );
            return response.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
            queryClient.invalidateQueries({ queryKey: EVENT_KEY(id) });
        },
    });
}

/**
 * RSVP to an INFO type event (increment attendees count)
 */
export function useEventRsvp() {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<Event>, Error, string>({
        mutationFn: async (id: string) => {
            const response = await apiClient.post<ApiResponse<Event>>(
                API_ENDPOINTS.EVENTS.RSVP(id)
            );
            return response.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
            queryClient.invalidateQueries({ queryKey: EVENT_KEY(id) });
            queryClient.invalidateQueries({ queryKey: ['events', 'slug'] });
            queryClient.invalidateQueries({ queryKey: PUBLIC_STATS_KEY });
        },
    });
}

/**
 * Close event response type
 */
export interface CloseEventResult {
    attended: number;
    noShow: number;
    total: number;
}

/**
 * Close an event (mark remaining APPROVED bookings as NO_SHOW)
 * Only available for past events that haven't been closed yet
 */
export function useCloseEvent() {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<CloseEventResult>, Error, string>({
        mutationFn: async (id: string) => {
            const response = await apiClient.patch<ApiResponse<CloseEventResult>>(
                API_ENDPOINTS.EVENTS.CLOSE(id)
            );
            return response.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
            queryClient.invalidateQueries({ queryKey: EVENT_KEY(id) });
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
            // Invalidate public stats (totalImpact includes event attendees)
            queryClient.invalidateQueries({ queryKey: PUBLIC_STATS_KEY });
        },
    });
}

export default useEvents;

